import { isTrivia, ScanErrorKind, type Token, TokenKind } from '../../json/tokens';
import type { TextEdit } from '../../text/textEdit';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * Put back the ONE quote a hand edit lost, in the three places it gets lost:
 *
 *   "name": Kaito Components K.K.",    → "name": "Kaito Components K.K.",   (value, opening)
 *   "id: "sup-lumen",                  → "id": "sup-lumen",                 (key, closing)
 *   "name": "Kaito Components K.K.,    → "name": "Kaito Components K.K.",   (value, closing)
 *
 * WHY THIS IS NOT THE quote-unquoted-value RULE v1 REFUSES (corpus B15/B16). That rule read a
 * bare word and decided it was a string — `{"a":NaN}` became `{"a":"NaN"}` — which is a
 * guess about TYPE. This rule never decides a type. It fires only where the author's own
 * keystrokes already say "string": a quote that is still there, on the same line, with its
 * partner missing. The scanner hands that surviving quote back as an UNTERMINATED string
 * running to the end of the line, and that token is the anchor for all three shapes:
 *
 *   - its remainder is nothing but an optional comma, so the quote CLOSED something (shapes
 *     1 and 2), and the text between it and the nearest delimiter on the same line is what it
 *     closed; or
 *   - it sits in value position and its own text ends in a comma with the next property or a
 *     closer after it, so the quote OPENED something and lost its closer before the comma
 *     (shape 3).
 *
 * Under those conditions there is exactly one quote missing and exactly one place it can go,
 * which is what `safe` means in ADR-0012. Everything else is left alone — the raw line break
 * inside a string (`{"a":"x⏎y"}`, which wants `\n`, not a quote, and which
 * `close-unterminated-string` declines for the same reason), a bare word with no surviving
 * quote (`{"a":b}`, B15), a run that is only a JSON literal (`"a": true",` — is the quote
 * stray, or the type wrong?), and a closer sharing the line with the surviving quote, whose
 * bracket is then inside the unterminated token and would be double-closed by
 * `close-open-containers` in the same pass.
 *
 * `JSON.stringify` writes every literal, so whatever the recovered text holds — a backslash,
 * a control character — comes out as a correctly escaped string rather than a second
 * broken document.
 */
export const restoreMissingQuoteRule: RepairRule = {
  id: 'restore-missing-quote',
  confidence: 'safe',
  // Ahead of `insert-missing-colon` (26), which reads `Kaito Components K.K.` as a property
  // name missing its colon and would write `Kaito Components: K.K."` into the same span.
  priority: 22,
  resolves: ['string/unterminated'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    const { source, tokens } = ctx;
    const arrayCommas = commasInsideArrays(tokens);
    const proposals: RepairProposal[] = [];

    for (let i = 0; i < tokens.length; i += 1) {
      const dangling = tokens[i];
      if (dangling?.kind !== TokenKind.String) continue;
      if (dangling.scanError !== ScanErrorKind.UnterminatedString) continue;
      if (!endsLine(tokens, i)) continue;

      const proposal =
        closingQuoteOfValue(source, tokens, i, arrayCommas) ??
        closingQuoteOfKey(source, tokens, i) ??
        openingQuoteLostItsCloser(source, tokens, i, arrayCommas);
      if (proposal !== undefined) proposals.push(proposal);
    }

    return proposals;
  },
};

/** What follows the surviving quote when it closed something: an optional comma. */
const CLOSER_REMAINDER = /^[ \t\r]*,?[ \t\r]*$/;

/**
 * Shape 1 — `"name": Kaito Components K.K.",`. The value lost its OPENING quote: a run of
 * bare tokens sits between a value-position delimiter and the surviving quote.
 */
function closingQuoteOfValue(
  source: string,
  tokens: readonly Token[],
  danglingIndex: number,
  arrayCommas: ReadonlySet<Token>,
): RepairProposal | undefined {
  const dangling = tokens[danglingIndex];
  if (
    dangling === undefined ||
    !CLOSER_REMAINDER.test(source.slice(dangling.from + 1, dangling.to))
  ) {
    return undefined;
  }

  const run = bareRunBefore(tokens, danglingIndex);
  if (run === undefined) return undefined;
  // A run of nothing but JSON literals (`true`, `12`, `null`) is the one place where the
  // surviving quote might be the stray character instead of the missing one.
  if (!run.tokens.some((token) => token.kind === TokenKind.Unknown)) return undefined;
  if (!isValueDelimiter(tokens, run.beforeIndex, arrayCommas)) return undefined;

  const first = run.tokens[0];
  if (first === undefined) return undefined;
  const text = source.slice(first.from, dangling.from);

  return proposal(first.from, dangling, {
    from: first.from,
    to: dangling.from + 1,
    insert: JSON.stringify(text),
  });
}

/**
 * Shape 2 — `"id: "sup-lumen",`. The KEY lost its closing quote, so the scanner read
 * `"id: "` as the key and the value's opening quote as its end. Recognised by a key-position
 * string whose text ends in a colon, followed on the same line by a bare run and the
 * surviving quote.
 */
function closingQuoteOfKey(
  source: string,
  tokens: readonly Token[],
  danglingIndex: number,
): RepairProposal | undefined {
  const dangling = tokens[danglingIndex];
  if (
    dangling === undefined ||
    !CLOSER_REMAINDER.test(source.slice(dangling.from + 1, dangling.to))
  ) {
    return undefined;
  }

  const run = bareRunBefore(tokens, danglingIndex);
  const key = run?.before;
  if (
    run === undefined ||
    !run.sameLine ||
    key?.kind !== TokenKind.String ||
    key.scanError !== ScanErrorKind.None
  ) {
    return undefined;
  }
  if (!isKeyPosition(tokens, run.beforeIndex)) return undefined;

  const decoded = decodeString(source.slice(key.from, key.to));
  const parts = decoded === undefined ? null : KEY_WITH_COLON.exec(decoded);
  const first = run.tokens[0];
  if (parts === null || first === undefined) return undefined;
  const [, name = '', gap = ''] = parts;

  const value = source.slice(first.from, dangling.from);
  const between = source.slice(key.to, first.from);
  const insert = `${JSON.stringify(name)}:${gap}${between}${JSON.stringify(value)}`;

  return proposal(key.from, dangling, { from: key.from, to: dangling.from + 1, insert });
}

/** `id: ` → name `id`, gap ` `. The LAST colon, so a name may itself hold one: `a:b: `. */
const KEY_WITH_COLON = /^(.*?\S)[ \t]*:([ \t]*)$/s;

/**
 * Shape 3 — `"name": "Kaito Components K.K.,`. The value lost its CLOSING quote, so the
 * string runs to the end of the line and swallows the comma. Only when the line ends in that
 * comma and the next significant token starts the next property, or when the next token
 * closes the container — anything else is the raw-line-break case, which is not ours.
 */
function openingQuoteLostItsCloser(
  source: string,
  tokens: readonly Token[],
  danglingIndex: number,
  arrayCommas: ReadonlySet<Token>,
): RepairProposal | undefined {
  const dangling = tokens[danglingIndex];
  if (dangling === undefined) return undefined;

  const before = previousSignificant(tokens, danglingIndex);
  if (before === undefined || !isValueDelimiter(tokens, before.index, arrayCommas)) {
    return undefined;
  }

  const next = nextSignificant(tokens, danglingIndex);
  const body = source.slice(dangling.from + 1, dangling.to);
  const withComma = /^(.*?\S)[ \t]*,[ \t\r]*$/s.exec(body);
  const bare = /^(.*?\S)[ \t\r]*$/s.exec(body);

  let content: string | undefined;
  if (withComma !== null && next?.kind === TokenKind.String) content = withComma[1];
  else if (withComma === null && bare !== null && isCloserToken(next)) content = bare[1];
  if (content === undefined) return undefined;

  // `"x\,` — the backslash would escape the quote we write. It is the user's data; leave it.
  if (content.endsWith('\\')) return undefined;

  // One quote, inserted. The literal's bytes — escapes included — are the user's, and
  // already a string body; nothing is re-encoded.
  const end = dangling.from + 1 + content.length;
  return proposal(dangling.from, dangling, { from: end, to: end, insert: '"' });
}

// ── shared ──────────────────────────────────────────────────────────────────────────────

function proposal(spanFrom: number, dangling: Token, edit: TextEdit): RepairProposal {
  return {
    ruleId: 'restore-missing-quote',
    span: { from: spanFrom, to: dangling.to },
    edits: [edit],
    labelCode: 'fix.restore-missing-quote',
    resolves: 'string/unterminated',
  };
}

/** `detect` never throws (the RepairRule contract); a token we cannot decode is not ours. */
function decodeString(literal: string): string | undefined {
  try {
    const value: unknown = JSON.parse(literal);
    return typeof value === 'string' ? value : undefined;
  } catch {
    return undefined;
  }
}

const BARE: ReadonlySet<TokenKind> = new Set([
  TokenKind.Unknown,
  TokenKind.Number,
  TokenKind.True,
  TokenKind.False,
  TokenKind.Null,
]);

interface BareRun {
  /** In document order. Never empty. */
  readonly tokens: readonly Token[];
  /** The significant token in front of the run. */
  readonly before: Token;
  readonly beforeIndex: number;
  /**
   * Whether `before` shares the run's line. A pretty-printed array puts each element's
   * delimiter at the end of the PREVIOUS line, so a value may look back across it; a key
   * that lost its quote never can, because the run is its own value on its own line.
   */
  readonly sameLine: boolean;
}

/**
 * The bare tokens directly in front of `index` — all on its line, never across a line break
 * or a comment — and the significant token in front of them, which may be on a line above.
 */
function bareRunBefore(tokens: readonly Token[], index: number): BareRun | undefined {
  const run: Token[] = [];
  let at = index;

  for (;;) {
    const previous = previousOnLine(tokens, at);
    if (previous === undefined) {
      const across = run.length === 0 ? undefined : previousSignificant(tokens, at);
      return across === undefined
        ? undefined
        : { tokens: run, before: across.token, beforeIndex: across.index, sameLine: false };
    }
    if (!BARE.has(previous.token.kind)) {
      return run.length === 0
        ? undefined
        : { tokens: run, before: previous.token, beforeIndex: previous.index, sameLine: true };
    }
    run.unshift(previous.token);
    at = previous.index;
  }
}

function previousSignificant(
  tokens: readonly Token[],
  index: number,
): { readonly token: Token; readonly index: number } | undefined {
  for (let i = index - 1; i >= 0; i -= 1) {
    const token = tokens[i];
    if (token === undefined) return undefined;
    if (!isTrivia(token)) return { token, index: i };
  }
  return undefined;
}

/** The previous significant token, unless a line break or a comment comes first. */
function previousOnLine(
  tokens: readonly Token[],
  index: number,
): { readonly token: Token; readonly index: number } | undefined {
  for (let i = index - 1; i >= 0; i -= 1) {
    const token = tokens[i];
    if (token === undefined) return undefined;
    if (token.kind === TokenKind.Whitespace) continue;
    if (isTrivia(token)) return undefined;
    return { token, index: i };
  }
  return undefined;
}

function nextSignificant(tokens: readonly Token[], index: number): Token | undefined {
  for (let i = index + 1; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (token !== undefined && !isTrivia(token)) return token;
  }
  return undefined;
}

/** The unterminated token runs to a line break (or the end), never into more code. */
function endsLine(tokens: readonly Token[], index: number): boolean {
  const next = tokens[index + 1];
  return next === undefined || next.kind === TokenKind.LineBreak || next.kind === TokenKind.EOF;
}

function isCloserToken(token: Token | undefined): boolean {
  return token?.kind === TokenKind.CloseBrace || token?.kind === TokenKind.CloseBracket;
}

/**
 * `"key":` in an object, or `[` / `,` in an array: what comes next is a value.
 *
 * A colon counts only behind a clean string, because a bare run can hold a colon of its own:
 * in `"path": C:\temp",` the `:` after `C` is data, and reading it as the delimiter would
 * quote `\temp` alone and leave `C:` in front of it.
 */
function isValueDelimiter(
  tokens: readonly Token[],
  index: number,
  arrayCommas: ReadonlySet<Token>,
): boolean {
  const token = tokens[index];
  if (token === undefined) return false;
  if (token.kind === TokenKind.OpenBracket) return true;
  if (token.kind === TokenKind.Comma) return arrayCommas.has(token);
  if (token.kind !== TokenKind.Colon) return false;

  const key = previousSignificant(tokens, index)?.token;
  return key?.kind === TokenKind.String && key.scanError === ScanErrorKind.None;
}

/** `{` or an object's `,` in front, across lines: what comes next is a property name. */
function isKeyPosition(tokens: readonly Token[], index: number): boolean {
  for (let i = index - 1; i >= 0; i -= 1) {
    const token = tokens[i];
    if (token === undefined) return false;
    if (isTrivia(token)) continue;
    return token.kind === TokenKind.OpenBrace || token.kind === TokenKind.Comma;
  }
  return false;
}

/**
 * The commas whose innermost container is an array — one forward pass with a stack. A
 * broken document can leave the stack wrong, which only ever costs a proposal (an array
 * comma read as an object's), never a wrong edit: shape 1 and 3 still need their anchor.
 */
function commasInsideArrays(tokens: readonly Token[]): ReadonlySet<Token> {
  const commas = new Set<Token>();
  const stack: TokenKind[] = [];

  for (const token of tokens) {
    if (token.kind === TokenKind.OpenBrace || token.kind === TokenKind.OpenBracket) {
      stack.push(token.kind);
    } else if (token.kind === TokenKind.CloseBrace || token.kind === TokenKind.CloseBracket) {
      stack.pop();
    } else if (
      token.kind === TokenKind.Comma &&
      stack[stack.length - 1] === TokenKind.OpenBracket
    ) {
      commas.add(token);
    }
  }
  return commas;
}
